import axios from 'axios';
import { SeverityBands } from '../lib/severity';
import {
  Risk,
  RiskComment,
  RiskAttachment,
  CreateRiskData,
  UpdateRiskData,
  CreateRiskCommentData,
  RiskQueryFilters,
  RiskStats,
  PaginatedRisks,
  RegisterApiParams,
} from '../types/risk.types';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api';

// Create axios instance with auth interceptor
const api = axios.create({
  baseURL: `${API_URL}/risks`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      // Token might be expired, redirect to login
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Asks for the whole list in one request. The server clamps this to its
// MAX_PAGE_SIZE setting, so the page loop below is what actually guarantees
// completeness; this just keeps it to one round trip in the normal case.
const PAGE_SIZE = 500;

class RiskService {
  // Risk methods
  // Callers treat this as "every risk I can see", and the Related Risk picker
  // in the task dialog depends on that: a risk it does not return cannot be
  // chosen. It used to read page one and drop the rest, so 38 of 58 risks
  // were unreachable. page_size asks for the lot in one request and the loop
  // covers a register larger than the server's MAX_PAGE_SIZE.
  async getRisks(filters?: RiskQueryFilters): Promise<Risk[]> {
    const params: Record<string, unknown> = {
      ...filters,
      page_size: PAGE_SIZE,
      // Add timestamp to prevent caching
      _t: Date.now(),
    };
    const collected: Risk[] = [];
    let page = 1;
    for (;;) {
      const response = await api.get('/items/', { params: { ...params, page } });
      const body = response.data;
      // A non-paginated response is the whole answer.
      if (!body?.results) return Array.isArray(body) ? body : [];
      collected.push(...body.results);
      if (!body.next) return collected;
      page += 1;
    }
  }

  async getRisksPage(params: RegisterApiParams): Promise<PaginatedRisks> {
    const response = await api.get('/items/', { params });
    return response.data;
  }

  async getRisk(id: string): Promise<Risk> {
    const response = await api.get<Risk>(`/items/${id}/`);
    return response.data;
  }

  async createRisk(data: CreateRiskData): Promise<Risk> {
    // Transform dates from ISO strings to backend format
    const transformedData = {
      ...data,
      treatment_due_date: data.treatment_due_date ? data.treatment_due_date.split('T')[0] : undefined,
      next_review_date: data.next_review_date ? data.next_review_date.split('T')[0] : undefined,
      last_reviewed_date: data.last_reviewed_date ? data.last_reviewed_date.split('T')[0] : undefined,
    };
    const response = await api.post<Risk>('/items/', transformedData);
    return response.data;
  }

  async updateRisk(id: string, data: UpdateRiskData): Promise<Risk> {
    // Transform dates from ISO strings to backend format and clean up data
    const transformedData: any = {};
    
    // Only include fields that are actually being updated
    Object.keys(data).forEach(key => {
      const value = (data as any)[key];
      // Skip undefined values
      if (value !== undefined) {
        if (key === 'treatment_due_date' || key === 'next_review_date' || key === 'last_reviewed_date') {
          transformedData[key] = value ? value.split('T')[0] : null;
        } else if (key === 'inherent_notes' || key === 'residual_notes' || key === 'treatment_details' || key === 'treatment_owner') {
          // For optional text fields, convert empty strings to null
          transformedData[key] = value || null;
        } else if (key === 'company' || key === 'team' || key === 'department') {
          // For organization fields, only include if they have a real value (not empty string)
          if (value && value !== '') {
            transformedData[key] = value;
          }
          // Don't include at all if empty or undefined
        } else {
          // Include other values as-is, but convert empty strings to null for optional fields
          transformedData[key] = value === '' ? null : value;
        }
      }
    });

    const response = await api.patch<Risk>(`/items/${id}/`, transformedData);
    return response.data;
  }

  // Archive, not delete. The row leaves the register and the record stays,
  // which is the right default for a register whose whole job is keeping
  // evidence that a risk existed.
  async archiveRisk(id: string): Promise<Risk> {
    const response = await api.post<Risk>(`/items/${id}/archive/`);
    return response.data;
  }

  async restoreRisk(id: string): Promise<Risk> {
    const response = await api.post<Risk>(`/items/${id}/restore/`);
    return response.data;
  }

  async deleteRisk(id: string): Promise<void> {
    await api.delete(`/items/${id}/`);
  }

  async getRiskStats(filters?: Omit<RiskQueryFilters, 'search'>): Promise<RiskStats> {
    const response = await api.get<RiskStats>('/items/stats/', { params: filters });
    return response.data;
  }

  async getSeverityBands(): Promise<SeverityBands> {
    const response = await api.get<SeverityBands>('/items/severity-bands/');
    return response.data;
  }

  async getCategories(): Promise<string[]> {
    const response = await api.get('/items/categories/');
    return response.data;
  }

  async getVendorNotes(): Promise<string[]> {
    const response = await api.get('/items/vendor-notes/');
    return response.data;
  }

  async exportRisks(format: 'csv' | 'pdf', filters?: RiskQueryFilters): Promise<Blob> {
    const endpoint = format === 'csv' ? '/items/export_csv/' : '/items/export_pdf/';
    const response = await api.get(endpoint, {
      params: filters,
      responseType: 'blob',
    });
    return response.data;
  }

  // Comment methods
  async getRiskComments(riskId: string): Promise<RiskComment[]> {
    const response = await api.get<RiskComment[]>(`/items/${riskId}/comments/`);
    return response.data;
  }

  async addRiskComment(riskId: string, data: CreateRiskCommentData): Promise<RiskComment> {
    const response = await api.post<RiskComment>(`/items/${riskId}/comments/`, data);
    return response.data;
  }

  async deleteRiskComment(riskId: string, commentId: string): Promise<void> {
    await api.delete(`/items/${riskId}/comments/${commentId}/`);
  }

  // Attachment methods
  async getRiskAttachments(riskId: string): Promise<RiskAttachment[]> {
    const response = await api.get<RiskAttachment[]>(`/items/${riskId}/attachments/`);
    return response.data;
  }

  async uploadRiskAttachment(riskId: string, file: File): Promise<RiskAttachment> {
    const formData = new FormData();
    formData.append('file', file);

    const response = await api.post<RiskAttachment>(
      `/items/${riskId}/attachments/`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return response.data;
  }

  async downloadRiskAttachment(riskId: string, attachmentId: string): Promise<Blob> {
    const response = await api.get(`/items/${riskId}/attachments/${attachmentId}/download/`, {
      responseType: 'blob',
    });
    return response.data;
  }

  async deleteRiskAttachment(riskId: string, attachmentId: string): Promise<void> {
    await api.delete(`/items/${riskId}/attachments/${attachmentId}/`);
  }

  // Risk matrix
  // Response shape per backend risk_matrix action: matrices keyed "likelihood,impact".
  async getRiskMatrix(filters?: Omit<RiskQueryFilters, 'search'>): Promise<{
    matrix_inherent: Record<string, number>;
    matrix_residual: Record<string, number>;
    total_risks: number;
    average_risk_reduction: number;
  }> {
    const response = await api.get('/items/risk_matrix/', { params: filters });
    return response.data;
  }

  async exportRiskMatrix(type: 'inherent' | 'residual' = 'residual'): Promise<Blob> {
    const response = await axios.get(`${API_URL}/common/export/risk-matrix/pdf/`, {
      params: { type },
      responseType: 'blob',
      headers: {
        Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
      },
    });
    return response.data;
  }

  // Bulk operations
  async bulkUpdateRisks(
    riskIds: string[],
    data: Partial<UpdateRiskData>
  ): Promise<{ updated: number; errors: string[] }> {
    const response = await api.post('/items/bulk_update/', {
      riskIds,
      data,
    });
    return response.data;
  }

  async bulkDeleteRisks(riskIds: string[]): Promise<{ deleted: number; errors: string[] }> {
    const response = await api.post('/items/bulk_delete/', { riskIds });
    return response.data;
  }
}

const riskService = new RiskService();
export default riskService;